/*!
 * AudioLogEngine — the recording half of /audio-logger: one microphone, a
 * recorder per segment, rotation without a gap, pause by silence (VOX), and a
 * level reading for every segment. No DOM in here; the page listens to events.
 *
 * What it guarantees.
 *   • Every chunk the recorder emits goes to the store the moment it arrives
 *     (AudioLogStore). A segment is «the bytes in the store», never «an array
 *     in this object», so a page that dies mid-segment leaves a playable take.
 *   • A new segment's recorder is running BEFORE the old one stops; where a
 *     second recorder on a live track is refused (Android / Yandex,
 *     NotSupportedError — Sentry N7) the old one is stopped first and the new
 *     one starts after it, with a fraction of a second missing.
 *   • The clock that turns segments is the recorder's own chunk stream, with a
 *     timer only as a second trigger: a hidden tab delays timers by up to a
 *     minute, the recorder keeps delivering every second.
 *   • Level and VOX come from the level-meter worklet (audio thread), not from
 *     requestAnimationFrame: rAF stops in a hidden tab, and a logger is a tab
 *     nobody is looking at. Before this the VOX check lived in the waveform's
 *     rAF loop — in a background tab it froze in whatever state it was in.
 *     No worklet → a 50-ms timer over an AnalyserNode.
 *
 * Time. «Armed» time is wall time while the logger is recording or waiting on
 * silence — segments turn on it. «Recorded» time is what is in the file — the
 * timers show it.
 *
 * Level. For each segment: the true peak, the level (98th percentile of 50-ms
 * window peaks — the voice, robust to one pop) and the floor (10th percentile
 * of window RMS). These are TakeLevel's definitions, kept as 1-dB histograms so
 * an hour costs 200 counters. A meter that never delivered a window reports no
 * level at all — «unknown» is not «quiet».
 *
 * window.AudioLogEngine.create({slug, store, openMic, closeMic, settings, emit, meterUrls})
 *   settings() → {bps, channels, intervalMin, vox, voxDb, quality, deviceId, name(num, startedAtMs, ext)}
 *   engine.start() / pause() / unpause() / stop() → state changes
 *   engine.state            'idle' | 'recording' | 'paused' | 'stopped'
 *   engine.times()          {totalMs, segMs, segNum}
 *   engine.level()          {db, at} — the last window's peak
 *   engine.ctx / .source / .analyser — for the page's waveform and sound marks
 * Events through emit(type, data):
 *   'state' {state}  ·  'segment-open' {num}  ·  'segment' {meta, why}
 *   'vox' {paused}   ·  'level' {db, rmsDb}   ·  'mic-lost' {why}
 *   'format' {mime, ext, family, container}   ·  'muted' {muted}
 * Guard: tests/js/audio-log-engine.test.mjs.
 */
(function (global) {
'use strict';
if (global.AudioLogEngine) return;

var WIN_S = 0.05;              // meter window — TakeLevel's frame
var VOX_HOLD_MS = 500;         // silence this long before the recorder pauses
var DB_MIN = -100;

function dbOf(x) { return x > 1e-9 ? 20 * Math.log10(x) : -180; }

/* 1-dB histograms of window peaks and window RMS. */
function Stats() { this.pk = new Uint32Array(101); this.rm = new Uint32Array(101); this.n = 0; this.peak = 0; }
Stats.prototype.add = function (peak, rms) {
    var a = Math.round(dbOf(peak)), b = Math.round(dbOf(rms));
    this.pk[Math.max(0, Math.min(100, a - DB_MIN))]++;
    this.rm[Math.max(0, Math.min(100, b - DB_MIN))]++;
    this.n++;
    if (peak > this.peak) this.peak = peak;
};
function pct(hist, n, p) {
    var want = Math.round((n - 1) * p), seen = 0;
    for (var i = 0; i < hist.length; i++) { seen += hist[i]; if (seen > want) return i + DB_MIN; }
    return DB_MIN;
}
Stats.prototype.summary = function () {
    if (!this.n) return null;
    var levelDb = pct(this.pk, this.n, 0.98), noiseDb = pct(this.rm, this.n, 0.10);
    var out = { peakDb: Math.round(dbOf(this.peak) * 10) / 10, levelDb: levelDb, noiseDb: noiseDb, snrDb: levelDb - noiseDb, windows: this.n };
    var TL = global.TakeLevel;
    out.boostDb = (TL && TL.normalizeGainDb) ? TL.normalizeGainDb(out) : 0;
    return out;
};

function tel(f, data) {
    try { if (typeof global.__ae === 'function') global.__ae('feature_use', Object.assign({ slug: 'audio-logger', f: f }, data || {})); } catch (_) {}
}

function Engine(o) {
    this.slug = o.slug || 'audio-logger';
    this.store = o.store;
    this._openMic = o.openMic;
    this._closeMic = o.closeMic || function () {};
    this._settings = o.settings;
    this._emit = o.emit || function () {};
    this._meterUrls = (o.meterUrls || []).filter(Boolean);
    this._MR = o.MediaRecorder || global.MediaRecorder;
    this._now = o.now || function () { return Date.now(); };
    this.state = 'idle';
    this.stream = null; this.ctx = null; this.source = null; this.analyser = null;
    this.cur = null;
    this.segNum = 0;
    this.run = '';
    this._reset();
}

Engine.prototype._reset = function () {
    this._runSince = 0; this._armedSince = 0;
    this._segRecMs = 0; this._totalRecMs = 0; this._segArmedMs = 0;
    this._voxPaused = false; this._quietMs = 0;
    this._lvl = { db: null, at: 0 };
    this._rotTimer = null;
};

Engine.prototype.emit = function (type, data) {
    try { this._emit(type, data || {}); } catch (e) { try { console.error('[audio-logger]', type, e); } catch (_) {} }
};

/* ── clocks ── */
Engine.prototype._runStart = function () { if (!this._runSince) this._runSince = this._now(); };
Engine.prototype._runStop = function () {
    if (!this._runSince) return;
    var d = this._now() - this._runSince;
    this._segRecMs += d; this._totalRecMs += d; this._runSince = 0;
};
Engine.prototype._armStart = function () { if (!this._armedSince) this._armedSince = this._now(); };
Engine.prototype._armStop = function () {
    if (!this._armedSince) return;
    this._segArmedMs += this._now() - this._armedSince; this._armedSince = 0;
};
Engine.prototype._armedMs = function () {
    return this._segArmedMs + (this._armedSince ? this._now() - this._armedSince : 0);
};
Engine.prototype.times = function () {
    var live = this._runSince ? this._now() - this._runSince : 0;
    return { totalMs: this._totalRecMs + live, segMs: this._segRecMs + live, segNum: this.segNum };
};
Engine.prototype.level = function () { return this._lvl; };

/* ── a segment and its recorder ── */
Engine.prototype._openSegment = function (num) {
    var self = this, s = this._settings(), F = global.AudioLogFormat;
    var asked = F.pick(this._MR);
    var opts = { audioBitsPerSecond: s.bps };
    if (asked.mime) opts.mimeType = asked.mime;
    var rec;
    try { rec = new this._MR(this.stream, opts); }
    catch (e) {
        if (!asked.mime) throw e;
        rec = new this._MR(this.stream, { audioBitsPerSecond: s.bps });
    }
    var startedAt = this._now();
    var seg = this.store.begin({ num: num, run: this.run, startedAt: startedAt, quality: s.quality || '', bps: s.bps, vox: s.vox ? 1 : 0 });
    var cur = { rec: rec, num: num, seg: seg, startedAt: startedAt, asked: asked, ending: false, why: '', durMs: null, stats: new Stats(), closed: false };
    rec.ondataavailable = function (e) {
        if (e.data && e.data.size > 0) seg.push(e.data);
        if (cur === self.cur) self._maybeRotate();
    };
    rec.onstop = function () { self._closeSegment(cur); };
    /* Firefox names the container only once the recorder has started. */
    rec.onstart = function () { if (cur === self.cur) self.emit('format', F.describe(rec.mimeType || asked.mime || '')); };
    try { rec.start(1000); }
    catch (e) { cur.closed = true; self.store.remove(seg.id); throw e; }
    return cur;
};

/* The new segment becomes current only after its recorder has started. */
Engine.prototype._adopt = function (cur) {
    var old = this.cur, rec = this.state === 'recording';
    this._runStop();
    if (old) old.durMs = this._segRecMs;
    if (rec) this._armStop();
    this._segRecMs = 0; this._segArmedMs = 0;
    this.cur = cur;
    this.segNum = cur.num;
    if (this._voxPaused) { try { if (cur.rec.state === 'recording') cur.rec.pause(); } catch (_) {} }
    else if (rec) this._runStart();
    if (rec) this._armStart();
    this._armRotation();
    this.emit('segment-open', { num: cur.num });
};

Engine.prototype._closeSegment = function (cur) {
    if (cur.closed) return;
    cur.closed = true;
    var self = this, F = global.AudioLogFormat, s = this._settings();
    var fmt = F.describe(cur.rec.mimeType || cur.asked.mime || '');
    if (cur.durMs === null) {                       // the browser stopped it, not us
        if (cur === this.cur) { this._runStop(); cur.durMs = this._segRecMs; } else cur.durMs = 0;
    }
    var why = cur.why || 'lost';
    var base = fmt.container === 'mp4' ? 'audio/mp4' : fmt.container === 'ogg' ? 'audio/ogg' : fmt.container === 'webm' ? 'audio/webm' : (cur.rec.mimeType || '');
    var name = s.name(cur.num, cur.startedAt, fmt.ext);
    this.emit('format', fmt);
    /* «lost»: the browser stopped the recorder — the microphone went away, or
       the page is leaving. The take is whole up to that point and says so. */
    cur.seg.end({ mime: base, ext: fmt.ext, family: fmt.family, container: fmt.container, name: name, durMs: Math.round(cur.durMs), level: cur.stats.summary(), why: why, interrupted: why === 'lost' ? 1 : 0 })
        .then(function (meta) {
            tel('al_segment', { n: meta.num, kb: Math.round(meta.bytes / 1024), s: Math.round(meta.durMs / 1000), why: why, ext: meta.ext,
                lvl: meta.level ? meta.level.levelDb : null, boost: meta.level ? meta.level.boostDb : null, vox: meta.vox, mode: self.store.mode });
            self.emit('segment', { meta: meta, why: why });
        });
    /* Stopped by the browser while still current — the microphone is gone. */
    if (!cur.ending && cur === this.cur) this._micLost('recorder_stopped');
};

Engine.prototype._micAlive = function () {
    var t = this.stream && this.stream.getAudioTracks ? this.stream.getAudioTracks() : [];
    for (var i = 0; i < t.length; i++) if (t[i].readyState === 'live') return true;
    return false;
};

/* ── rotation ── */
Engine.prototype._intervalMs = function () { return (parseInt(this._settings().intervalMin, 10) || 60) * 60000; };
Engine.prototype._armRotation = function () {
    var self = this;
    if (this._rotTimer) clearTimeout(this._rotTimer);
    this._rotTimer = null;
    if (this.state !== 'recording' && this.state !== 'starting') return;
    var left = Math.max(250, this._intervalMs() - this._armedMs());
    this._rotTimer = setTimeout(function () { self._rotTimer = null; self._maybeRotate(); if (!self._rotTimer) self._armRotation(); }, left);
};
Engine.prototype._maybeRotate = function () {
    if (this.state !== 'recording' || !this.cur || this._rotating) return;
    if (this._armedMs() < this._intervalMs()) return;
    this.rotate();
};
Engine.prototype.rotate = function () {
    if (this.state !== 'recording' || !this.cur) return;
    var self = this, old = this.cur, next;
    try { next = this._openSegment(old.num + 1); }
    catch (err) { this._rotateWithGap(old, err); return; }
    this._adopt(next);
    old.ending = true; old.why = 'rotate';
    try { old.rec.stop(); } catch (_) { self._closeSegment(old); }
};
Engine.prototype._rotateWithGap = function (old, err) {
    var self = this, live = this._micAlive();
    tel('rotate_gap', { action: 'rotate_gap', err: (err && err.name) || '', live: live ? 1 : 0 });
    if (!live) { this._micLost('rotate_dead_track'); return; }
    this._rotating = true;
    this._runStop(); old.durMs = this._segRecMs;
    old.ending = true; old.why = 'rotate';
    var after = function () {
        self._rotating = false;
        if (self.state !== 'recording' && self.state !== 'paused') return;
        try {
            var next = self._openSegment(old.num + 1);
            self._adopt(next);
            if (self.state === 'paused') { try { next.rec.pause(); } catch (_) {} }
        } catch (e2) { self.cur = null; self._micLost('rotate_restart_failed'); }
    };
    old.rec.addEventListener('stop', after, { once: true });
    try { old.rec.stop(); } catch (_) { after(); }
};

/* The microphone went away mid-recording (unplugged, taken by another program,
   permission withdrawn): what was recorded is already stored; the logger stops
   and says so. */
Engine.prototype._micLost = function (why) {
    if (this.state !== 'recording' && this.state !== 'paused') return;
    tel('mic_lost', { action: 'mic_lost', why: why, seg: this.segNum });
    this._stop('lost');
    this.emit('mic-lost', { why: why });
};

/* ── level + VOX ── */
Engine.prototype._onWindow = function (peak, rms, winMs) {
    this._lvl = { db: dbOf(peak), rmsDb: dbOf(rms), at: this._now() };
    this.emit('level', this._lvl);
    if (this.state !== 'recording' || !this.cur) return;
    var s = this._settings();
    if (!this._voxPaused) this.cur.stats.add(peak, rms);
    if (!s.vox) { if (this._voxPaused) this._voxSet(false); this._quietMs = 0; return; }
    if (dbOf(rms) < s.voxDb) {
        this._quietMs += winMs;
        if (!this._voxPaused && this._quietMs >= VOX_HOLD_MS) this._voxSet(true);
    } else {
        this._quietMs = 0;
        if (this._voxPaused) this._voxSet(false);
    }
};
Engine.prototype._voxSet = function (paused) {
    if (this._voxPaused === paused) return;
    this._voxPaused = paused;
    var rec = this.cur && this.cur.rec;
    try {
        if (paused) { this._runStop(); if (rec && rec.state === 'recording') rec.pause(); }
        else { if (rec && rec.state === 'paused') rec.resume(); if (this.state === 'recording') this._runStart(); }
    } catch (_) {}
    this.emit('vox', { paused: paused });
};

Engine.prototype._attachMeter = function () {
    var self = this, ctx = this.ctx;
    var fallback = function (why) {
        if (self._meterTimer || !self.ctx) return;
        if (why) tel('al_meter_fallback', { why: String(why).slice(0, 40) });
        var an = ctx.createAnalyser(); an.fftSize = 2048;
        self.source.connect(an);
        var buf = new Float32Array(an.fftSize), byteBuf = null, last = self._now();
        self._meterTimer = setInterval(function () {
            var now = self._now(), took = Math.max(50, Math.min(2000, now - last));
            last = now;
            if (!self.ctx || self.ctx.state !== 'running') return;       // a stopped clock is not evidence
            var peak = 0, sum = 0, i, v;
            if (an.getFloatTimeDomainData) { an.getFloatTimeDomainData(buf); for (i = 0; i < buf.length; i++) { v = buf[i]; if (v < 0) v = -v; if (v > peak) peak = v; sum += v * v; } }
            else { byteBuf = byteBuf || new Uint8Array(an.fftSize); an.getByteTimeDomainData(byteBuf); for (i = 0; i < byteBuf.length; i++) { v = (byteBuf[i] - 128) / 128; if (v < 0) v = -v; if (v > peak) peak = v; sum += v * v; } }
            self._onWindow(peak, Math.sqrt(sum / buf.length), took);   // a throttled timer covers more time per reading
        }, 50);
    };
    if (!ctx.audioWorklet || !global.AudioWorkletNode || !this._meterUrls.length) { fallback(''); return Promise.resolve(); }
    var load = global.AudioWorkletLoader
        ? global.AudioWorkletLoader.load(ctx, this._meterUrls, { slug: this.slug })
        : ctx.audioWorklet.addModule(this._meterUrls[0]);
    return Promise.resolve(load).then(function () {
        if (!self.ctx || self.ctx !== ctx) return;
        var node = new global.AudioWorkletNode(ctx, 'level-meter', { numberOfInputs: 1, numberOfOutputs: 0, processorOptions: { interval: WIN_S } });
        self.source.connect(node);
        var heard = false;
        node.port.onmessage = function (e) { var d = e.data; heard = true; if (d) self._onWindow(d.peak || 0, d.rms || 0, WIN_S * 1000); };
        self._meterNode = node;
        /* A meter node without an output is not pulled by every engine. If the
           clock runs and no window has come, read the level the other way. */
        setTimeout(function () {
            if (heard || self.ctx !== ctx || ctx.state !== 'running') return;
            try { node.port.onmessage = null; node.disconnect(); } catch (_) {}
            self._meterNode = null;
            fallback('silent_worklet');
        }, 4000);
    }).catch(function (e) { fallback((e && e.message) || 'worklet'); });
};

/* ── start / pause / resume / stop ── */
Engine.prototype.start = function () {
    var self = this;
    if (this.state === 'recording' || this.state === 'paused' || this.state === 'starting') return Promise.resolve(false);
    var s = this._settings();
    var constraints = { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: { ideal: s.channels || 1 } } };
    if (s.deviceId) constraints.audio.deviceId = { exact: s.deviceId };
    this.state = 'starting';
    return Promise.resolve(this._openMic(constraints)).then(function (stream) {
        self.stream = stream;
        var AC = global.AudioContext || global.webkitAudioContext;
        /* Default rate: the context only measures. A context at another rate
           than the microphone's is refused outright by some engines. */
        self.ctx = new AC();
        self.analyser = self.ctx.createAnalyser();
        self.analyser.fftSize = 256;
        self.source = self.ctx.createMediaStreamSource(stream);
        self.source.connect(self.analyser);
        self._reset();
        self.run = Date.now().toString(36);
        self.segNum = 0;
        var first = self._openSegment(1);
        self.state = 'recording';
        self._adopt(first);
        self._attachMeter();
        var tr = stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
        if (tr && tr.addEventListener) {
            tr.addEventListener('mute', function () { tel('al_track_mute', { hidden: global.document && document.hidden ? 1 : 0 }); self.emit('muted', { muted: true }); });
            tr.addEventListener('unmute', function () { self.emit('muted', { muted: false }); });
        }
        self.emit('state', { state: 'recording' });
        return true;
    }).catch(function (err) {
        self._teardown();
        self.state = 'idle';
        throw err;
    });
};

Engine.prototype.pause = function () {
    if (this.state !== 'recording') return;
    this._runStop(); this._armStop();
    this._voxPaused = false; this._quietMs = 0;
    var rec = this.cur && this.cur.rec;
    try { if (rec && rec.state === 'recording') rec.pause(); } catch (_) {}
    if (this._rotTimer) { clearTimeout(this._rotTimer); this._rotTimer = null; }
    this.state = 'paused';
    this.emit('state', { state: 'paused' });
};

/* Not «resume»: a tool page that calls `<name>.resume()` is read by the site's
   lint as an AudioContext being resumed outside a gesture. */
Engine.prototype.unpause = function () {
    if (this.state !== 'paused') return;
    var rec = this.cur && this.cur.rec;
    try { if (rec && rec.state === 'paused') rec.resume(); } catch (_) {}
    this.state = 'recording';
    this._quietMs = 0;
    this._armStart(); this._runStart();
    this._armRotation();
    this.emit('state', { state: 'recording' });
    this._maybeRotate();
};

Engine.prototype.stop = function () { this._stop('stop'); };

Engine.prototype._stop = function (why) {
    if (this.state !== 'recording' && this.state !== 'paused') return;
    this._runStop(); this._armStop();
    if (this._rotTimer) { clearTimeout(this._rotTimer); this._rotTimer = null; }
    var cur = this.cur;
    this.state = 'stopped';
    if (cur && !cur.closed) {
        if (cur.durMs === null) cur.durMs = this._segRecMs;
        cur.ending = true; cur.why = cur.why || why;
        var self = this;
        try { if (cur.rec.state !== 'inactive') cur.rec.stop(); else this._closeSegment(cur); }
        catch (_) { self._closeSegment(cur); }
    }
    this._teardown();
    this.emit('state', { state: 'stopped', why: why });
};

Engine.prototype._teardown = function () {
    if (this.ctx) this.emit('closing', {});          // the page releases what it hung on this context
    if (this._meterTimer) { clearInterval(this._meterTimer); this._meterTimer = null; }
    if (this._meterNode) { try { this._meterNode.port.onmessage = null; this._meterNode.disconnect(); } catch (_) {} this._meterNode = null; }
    if (this.stream) { try { this._closeMic(); } catch (_) {} this.stream = null; }
    var ctx = this.ctx;
    this.ctx = null; this.source = null; this.analyser = null;
    this._voxPaused = false;
    if (ctx && ctx.state !== 'closed') { try { ctx.close().catch(function () {}); } catch (_) {} }
};

global.AudioLogEngine = { create: function (o) { return new Engine(o); }, Stats: Stats, VOX_HOLD_MS: VOX_HOLD_MS };
if (typeof module !== 'undefined' && module.exports) module.exports = global.AudioLogEngine;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
